import { useCallback, useContext } from 'react';
import { LocaleContext, type LocaleContextValue } from './context';
import type { Bilingual } from './define';
import { formatMessage, type MessageValues } from './format';
import { intlLocale, type Locale } from './locale';

export { defineMessages } from './define';
export { LocaleProvider } from './LocaleProvider';
export { intlLocale, LOCALES, type Locale } from './locale';
export type { MessageValues } from './format';

export function useLocale(): LocaleContextValue & { readonly intl: string } {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used inside <LocaleProvider>');
  return { ...ctx, intl: intlLocale(ctx.locale) };
}

export type Translate<K extends string> = (key: K, values?: MessageValues) => string;

/** Translator bound to one message namespace: `const t = useT(homeMessages); t('hi', { name })`. */
export function useT<K extends string>(messages: Bilingual<K>): Translate<K> {
  const { locale } = useLocale();
  return useCallback(
    (key: K, values?: MessageValues) => formatMessage(locale, messages[locale][key], values),
    [locale, messages],
  );
}

/** Non-hook variant for code outside React (toasts from mutation callbacks, the SW…). */
export function translate<K extends string>(
  messages: Bilingual<K>,
  locale: Locale,
  key: K,
  values?: MessageValues,
): string {
  return formatMessage(locale, messages[locale][key], values);
}
